import {
  Background,
  BackgroundVariant,
  Edge,
  Handle,
  Node,
  NodeMouseHandler,
  NodeProps,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { useWorkOrder } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrder";
import {
  InvoiceStatus,
  WorkOrderAssignmentStatus,
  WorkOrderStatus,
} from "@/gql/graphql";
import { ReactNode, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { useTranslations } from "use-intl";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DocumentTextIcon } from "@heroicons/react/20/solid";
import { DocumentCheckIcon } from "@heroicons/react/16/solid";
import { FileClock } from "lucide-react";

export type WorkOrderFlowData = {
  id: string;
  reference: string;
  status: WorkOrderStatus;
  number_of_drivers?: number | null;
  relocation?: { id: string; reference: string } | null;
  currentQuote?: { id: string; reference: string } | null;
  currentInvoice?: {
    id: string;
    reference: string;
    status: InvoiceStatus;
  } | null;
  invoice_not_required?: boolean;
  assignments?: Array<{
    id: string;
    status: WorkOrderAssignmentStatus;
    proDriver?: { id: string; name: string | null } | null;
    payouts?: Array<{ id: string; paid_at?: string | null }>;
    pickup_date?: string | null;
    pickup_time?: string | null;
    delivery_date?: string | null;
    delivery_time?: string | null;
    driver_pick_up_at?: string | null;
    driver_delivered_at?: string | null;
  }>;
};

type FlowNodeData = {
  label: string;
  subtitle?: string;
  status:
    | "completed"
    | "current"
    | "upcoming"
    | "rejected"
    | "pending"
    | "overdue";
  isTerminal?: boolean;
  cardId?: string;
  fixedWidth?: number;
  isUnassigned?: boolean;
  relocationId?: string;
  compact?: string;
  tooltip?: ReactNode;
  Icon?: ReactNode;
  CompleteIcon?: ReactNode;
};

type FlowStage =
  | "request"
  | "quote"
  | "assigning-drivers"
  | "drivers-assigned"
  | "invoiced";

const stageOrder: FlowStage[] = [
  "request",
  "quote",
  "assigning-drivers",
  "drivers-assigned",
  "invoiced",
];

function getStageFromStatus(status: WorkOrderStatus): FlowStage {
  switch (status) {
    case WorkOrderStatus.QuoteRequested:
      return "request";
    case WorkOrderStatus.QuoteIssued:
    case WorkOrderStatus.QuoteRejected:
      return "quote";
    case WorkOrderStatus.AssigningDrivers:
      return "assigning-drivers";
    case WorkOrderStatus.DriversAssigned:
    case WorkOrderStatus.InProgress:
      return "drivers-assigned";
    case WorkOrderStatus.Completed:
    case WorkOrderStatus.Invoiced:
    case WorkOrderStatus.Archived:
      return "invoiced";
    default:
      return "request";
  }
}

function getNodeStatus(
  nodeStage: FlowStage,
  currentStatus: WorkOrderStatus,
): "completed" | "current" | "upcoming" | "rejected" {
  const currentStage = getStageFromStatus(currentStatus);

  if (
    currentStatus === WorkOrderStatus.QuoteRejected &&
    nodeStage === "quote"
  ) {
    return "rejected";
  }

  const currentIndex = stageOrder.indexOf(currentStage);
  const nodeIndex = stageOrder.indexOf(nodeStage);

  if (nodeIndex < currentIndex) return "completed";
  if (nodeIndex === currentIndex) return "current";
  return "upcoming";
}

function CompactNode({
  tooltip,
  isCompleted,
  isClickable,
  label,
  Icon,
  CompleteIcon,
}: {
  tooltip?: ReactNode;
  isCompleted: boolean;
  isClickable: boolean;
  label: string;
  Icon?: ReactNode;
  CompleteIcon?: ReactNode;
}) {
  const circle = (
    <div className="relative">
      <div
        className={`flex size-7 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm ${isClickable ? "cursor-pointer hover:opacity-80" : ""}`}
      >
        {isCompleted ? (
          CompleteIcon ? (
            <>{CompleteIcon}</>
          ) : (
            <CheckCircleIcon className="size-3.5 shrink-0 text-green-500" />
          )
        ) : Icon ? (
          <>{Icon}</>
        ) : (
          <span className="text-[9px] font-semibold text-gray-400">
            {label}
          </span>
        )}
      </div>
    </div>
  );

  if (!tooltip) {
    return circle;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{circle}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function FlowNode({ data }: NodeProps<Node<FlowNodeData>>) {
  const isCompleted = data.status === "completed";
  const isCurrent = data.status === "current";
  const isRejected = data.status === "rejected";
  const isPending = data.status === "pending";
  const isOverdue = data.status === "overdue";
  const isUnassigned = data.isUnassigned;
  const isClickable = !!(data.cardId || data.relocationId);

  const getIcon = () => {
    if (isUnassigned) {
      return (
        <div className="size-2.5 shrink-0 rounded-full border-2 border-gray-300" />
      );
    }
    if (isCompleted) {
      return <CheckCircleIcon className="size-3.5 shrink-0 text-green-500" />;
    }
    if (isRejected) {
      return <XCircleIcon className="size-3.5 shrink-0 text-red-500" />;
    }
    if (isOverdue) {
      return <ClockIcon className="size-3.5 shrink-0 text-orange-500" />;
    }
    if (isPending) {
      return (
        <div className="size-2.5 shrink-0 rounded-full border-2 border-dashed border-yellow-500" />
      );
    }
    if (isCurrent) {
      return (
        <div className="size-2.5 shrink-0 rounded-full border-2 border-blue-500 bg-blue-500" />
      );
    }
    return (
      <div className="size-2.5 shrink-0 rounded-full border-2 border-gray-300" />
    );
  };

  return (
    <>
      <Handle type="target" position={Position.Left} className="opacity-0" />
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="opacity-0"
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom"
        className="opacity-0"
      />
      {data.compact ? (
        <CompactNode
          tooltip={data.tooltip}
          isCompleted={isCompleted}
          isClickable={isClickable}
          label={data.compact}
          Icon={data.Icon}
          CompleteIcon={data.CompleteIcon}
        />
      ) : (
        <div
          style={data.fixedWidth ? { width: data.fixedWidth } : undefined}
          className={`flex min-w-[100px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 shadow-sm ${isClickable ? "cursor-pointer hover:bg-gray-50" : ""}`}
        >
          {getIcon()}
          <div className="min-w-0 flex-1">
            <span
              className={`block truncate text-[10px] font-medium ${
                isUnassigned
                  ? "text-gray-400"
                  : isCompleted || isCurrent || isPending
                    ? "text-gray-900"
                    : isRejected
                      ? "text-red-700"
                      : "text-gray-500"
              }`}
            >
              {data.label}
            </span>
            {data.subtitle ? (
              <span className="block truncate text-[9px] text-gray-400">
                {data.subtitle}
              </span>
            ) : null}
          </div>
        </div>
      )}
      <Handle type="source" position={Position.Right} className="opacity-0" />
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        className="opacity-0"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="opacity-0"
      />
    </>
  );
}

const nodeTypes = {
  flowNode: FlowNode,
};

export function WorkOrderFlow() {
  const workOrder = useWorkOrderRecord();
  return <WorkOrderFlowContent workOrder={workOrder} />;
}

export function WorkOrderFlowById({ workOrderId }: { workOrderId: string }) {
  const workOrder = useWorkOrder(workOrderId);
  return <WorkOrderFlowContent workOrder={workOrder} />;
}

function WorkOrderFlowContent({ workOrder }: { workOrder: WorkOrderFlowData }) {
  const currentStatus = workOrder.status;
  const driversRequired = workOrder.number_of_drivers ?? 1;
  const assignments = workOrder.assignments ?? [];
  const navigate = useNavigate();
  const routeParams = useParams({ strict: false });
  const t = useTranslations("workOrder");
  const { isAdmin } = useTenant();

  const { nodes, edges } = useMemo(() => {
    // Layout configuration
    const driverColWidth = 50;
    const hasRelocation = !!workOrder.relocation;
    const col0 = hasRelocation ? -150 : 0;
    const col1 = hasRelocation ? 0 : 0;
    const col2 = col1 + 130;
    const col3 = col2 + 150;
    const col4 = col3 + 135;
    const col5 = col4 + driverColWidth;
    const col6 = col5 + driverColWidth;
    const col7 = col6 + driverColWidth;
    const col8 = isAdmin ? col6 + 130 : col6 + 80;

    // Calculate vertical positions for driver nodes to fan out
    const driverNodeHeight = 50;
    const totalDriversHeight = driversRequired * driverNodeHeight;
    // Offset for nodes without subtitles to align with nodes that have them
    const noSubtitleOffset = 7;
    const driverStartY =
      -totalDriversHeight / 2 + driverNodeHeight / 2 + noSubtitleOffset;
    const mainRowY = 0;

    const nodes: Node<FlowNodeData>[] = [];

    // Add relocation node if work order has a relocation
    if (workOrder.relocation) {
      nodes.push({
        id: "relocation",
        type: "flowNode",
        position: { x: col0, y: mainRowY },
        data: {
          label: "Relocation",
          subtitle: workOrder.relocation.reference,
          status: "completed",
          relocationId: workOrder.relocation.id,
        },
      });
    }

    nodes.push({
      id: "request",
      type: "flowNode",
      position: { x: col1, y: mainRowY },
      data: {
        label: t("flow.request"),
        subtitle: workOrder.reference,
        status:
          currentStatus === WorkOrderStatus.QuoteRequested
            ? "pending"
            : getNodeStatus("request", currentStatus),
        cardId: "card-request",
      },
    });

    nodes.push({
      id: "quote",
      type: "flowNode",
      position: {
        x: col2,
        y: workOrder.currentQuote?.reference
          ? mainRowY
          : mainRowY + noSubtitleOffset,
      },
      data: {
        label: t("flow.quote"),
        subtitle: workOrder.currentQuote?.reference,
        status:
          currentStatus === WorkOrderStatus.QuoteIssued
            ? "pending"
            : getNodeStatus("quote", currentStatus),
        isTerminal: currentStatus === WorkOrderStatus.QuoteRejected,
        cardId: "card-quote",
      },
    });

    // Create driver sub-nodes (4 per driver: assignment, pickup, dropoff, paid)
    const driverNodes: Node<FlowNodeData>[] = [];
    const driverNodeWidth = 120;

    for (let i = 0; i < driversRequired; i++) {
      const assignment = assignments[i];
      const driverName = assignment?.proDriver?.name;
      const isAssigned = !!assignment;
      const isCompleted =
        assignment?.status === WorkOrderAssignmentStatus.Completed;

      const yPos = driverStartY + i * driverNodeHeight;

      // --- Assignment node ---
      let assignmentStatus: FlowNodeData["status"];
      if (isAssigned) {
        assignmentStatus = "completed";
      } else {
        const baseStatus = getNodeStatus("assigning-drivers", currentStatus);
        assignmentStatus = baseStatus === "current" ? "current" : "upcoming";
      }

      driverNodes.push({
        id: `driver-${i}-assignment`,
        type: "flowNode",
        position: { x: col3, y: yPos },
        data: {
          label: driverName ?? t("flow.driver", { number: i + 1 }),
          status: assignmentStatus,
          cardId: `card-driver-${i}`,
          fixedWidth: driverNodeWidth,
          isUnassigned: !isAssigned,
        },
      });

      // --- Picked Up node ---
      const pickupDone =
        isAssigned &&
        (assignment.status === WorkOrderAssignmentStatus.OnRoad ||
          assignment.status === WorkOrderAssignmentStatus.Delivered ||
          assignment.status === WorkOrderAssignmentStatus.ReceiptsUploaded ||
          assignment.status === WorkOrderAssignmentStatus.ReceiptsApproved ||
          assignment.status === WorkOrderAssignmentStatus.Completed);

      const pickupTooltip = assignment?.driver_pick_up_at ? (
        <span>
          Picked up at: {formatDateTimeLocal(assignment.driver_pick_up_at)} (UTC
          {dayjs().format("Z").replace(/:00$/, "")})
        </span>
      ) : undefined;

      driverNodes.push({
        id: `driver-${i}-pickup`,
        type: "flowNode",
        position: { x: col4, y: yPos },
        data: {
          label: "",
          status: pickupDone ? "completed" : "upcoming",
          cardId: `card-driver-${i}`,
          compact: "A",
          tooltip: pickupTooltip,
        },
      });

      // --- Dropped Off node ---
      const dropoffDone =
        isAssigned &&
        (assignment.status === WorkOrderAssignmentStatus.Delivered ||
          assignment.status === WorkOrderAssignmentStatus.ReceiptsUploaded ||
          assignment.status === WorkOrderAssignmentStatus.ReceiptsApproved ||
          assignment.status === WorkOrderAssignmentStatus.Completed);

      const dropoffTooltip = assignment?.driver_delivered_at ? (
        <span>
          Delivered at: {formatDateTimeLocal(assignment.driver_delivered_at)}{" "}
          (UTC{dayjs().format("Z").replace(/:00$/, "")})
        </span>
      ) : undefined;

      driverNodes.push({
        id: `driver-${i}-dropoff`,
        type: "flowNode",
        position: { x: col5, y: yPos },
        data: {
          label: "",
          status: dropoffDone ? "completed" : "upcoming",
          cardId: `card-driver-${i}`,
          compact: "B",
          tooltip: dropoffTooltip,
        },
      });

      // --- receipts uploaded node (admin only) ---
      if (isAdmin) {
        const receiptsApproved =
          assignment?.status === WorkOrderAssignmentStatus.ReceiptsApproved ||
          assignment?.status === WorkOrderAssignmentStatus.Completed;
        const receiptsPending =
          assignment?.status === WorkOrderAssignmentStatus.ReceiptsUploaded;

        driverNodes.push({
          id: `driver-${i}-receipts-uploaded`,
          type: "flowNode",
          position: { x: col6, y: yPos },
          data: {
            label: "",
            status: receiptsApproved ? "completed" : "upcoming",
            cardId: `card-driver-${i}`,
            Icon: receiptsPending ? (
              <FileClock className="size-3.5 shrink-0 text-yellow-500" />
            ) : (
              <DocumentTextIcon className="size-3.5 shrink-0 text-gray-400" />
            ),
            CompleteIcon: (
              <DocumentCheckIcon
                className={"size-3.5 shrink-0 text-green-500"}
              />
            ),
            compact: "C",
          },
        });
      }

      // --- Paid node (admin only) ---
      if (isAdmin) {
        const paidDone = isAssigned && isCompleted;

        driverNodes.push({
          id: `driver-${i}-paid`,
          type: "flowNode",
          position: { x: col7, y: yPos },
          data: {
            label: t("flow.paid"),
            status: paidDone ? "completed" : "upcoming",
            cardId: `card-driver-${i}`,
            compact: "$",
          },
        });
      }
    }

    nodes.push(...driverNodes);

    // Determine invoice node status based on invoice status
    let invoiceNodeStatus:
      | "completed"
      | "current"
      | "upcoming"
      | "rejected"
      | "pending"
      | "overdue";
    const invoice = workOrder.currentInvoice;
    if (!invoice && workOrder.invoice_not_required) {
      invoiceNodeStatus = "completed";
    } else if (!invoice || invoice.status === InvoiceStatus.Draft) {
      invoiceNodeStatus = "upcoming"; // gray
    } else if (invoice.status === InvoiceStatus.Issued) {
      invoiceNodeStatus = "pending"; // yellow
    } else if (invoice.status === InvoiceStatus.Paid) {
      invoiceNodeStatus = "completed"; // green
    } else if (invoice.status === InvoiceStatus.Overdue) {
      invoiceNodeStatus = "overdue"; // orange clock
    } else {
      invoiceNodeStatus = "upcoming"; // gray for voided or other
    }

    // Add invoiced node
    nodes.push({
      id: "invoiced",
      type: "flowNode",
      position: {
        x: col8,
        y: workOrder.currentInvoice?.reference
          ? mainRowY
          : mainRowY + noSubtitleOffset,
      },
      data: {
        label: t("flow.invoice"),
        subtitle: workOrder.currentInvoice?.reference,
        status: invoiceNodeStatus,
        isTerminal: true,
        cardId: "card-invoice",
      },
    });

    const edgeStyle = {
      stroke: "#d1d5db",
      strokeWidth: 1.5,
      strokeDasharray: "4 4",
    };

    const solidEdgeStyle = {
      stroke: "#d1d5db",
      strokeWidth: 1.5,
    };

    const edges: Edge[] = [];

    // Add edge from relocation to request if there's a relocation
    if (workOrder.relocation) {
      edges.push({
        id: "e-relocation-request",
        source: "relocation",
        target: "request",
        type: "smoothstep",
        style: edgeStyle,
      });
    }

    edges.push({
      id: "e-request-quote",
      source: "request",
      target: "quote",
      type: "smoothstep",
      style: edgeStyle,
    });

    // Add edges from quote through driver sub-nodes to invoice
    for (let i = 0; i < driversRequired; i++) {
      edges.push(
        {
          id: `e-quote-driver-${i}-assignment`,
          source: "quote",
          target: `driver-${i}-assignment`,
          type: "smoothstep",
          style: edgeStyle,
        },
        {
          id: `e-driver-${i}-assignment-pickup`,
          source: `driver-${i}-assignment`,
          target: `driver-${i}-pickup`,
          type: "smoothstep",
          style: solidEdgeStyle,
        },
        {
          id: `e-driver-${i}-pickup-dropoff`,
          source: `driver-${i}-pickup`,
          target: `driver-${i}-dropoff`,
          type: "smoothstep",
          style: solidEdgeStyle,
        },
        ...(isAdmin
          ? [
              {
                id: `e-driver-${i}-dropoff-receipts`,
                source: `driver-${i}-dropoff`,
                target: `driver-${i}-receipts-uploaded`,
                type: "smoothstep",
                style: solidEdgeStyle,
              },
              {
                id: `e-driver-${i}-receipts-paid`,
                source: `driver-${i}-receipts-uploaded`,
                target: `driver-${i}-paid`,
                type: "smoothstep",
                style: solidEdgeStyle,
              },
              {
                id: `e-driver-${i}-paid-invoiced`,
                source: `driver-${i}-paid`,
                target: "invoiced",
                type: "smoothstep",
                style: edgeStyle,
              },
            ]
          : [
              {
                id: `e-driver-${i}-dropoff-invoiced`,
                source: `driver-${i}-dropoff`,
                target: "invoiced",
                type: "smoothstep",
                style: edgeStyle,
              },
            ]),
      );
    }

    return { nodes, edges };
  }, [
    currentStatus,
    driversRequired,
    assignments,
    workOrder.reference,
    workOrder.currentQuote?.reference,
    workOrder.currentInvoice,
    workOrder.relocation,
    isAdmin,
    t,
  ]);

  const containerHeight = Math.max(180, driversRequired * 60 + 100);

  const onNodeClick: NodeMouseHandler<Node<FlowNodeData>> = useCallback(
    (_, node) => {
      const relocationId = node.data.relocationId;
      if (relocationId) {
        navigate({
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
          params: {
            tenant: routeParams.tenant!,
            orgId: routeParams.orgId!,
            supplierId: routeParams.supplierId,
            relocationId,
          },
        });
        return;
      }
      if (node.data.cardId) {
        navigate({
          to: ".",
          hash: node.data.cardId,
          replace: true,
          resetScroll: false,
        });
      }
    },
    [navigate, routeParams.tenant, routeParams.orgId, routeParams.supplierId],
  );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div style={{ height: containerHeight }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.05 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          nodesFocusable={false}
          edgesFocusable={false}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={16}
            size={1}
            color="#e5e7eb"
          />
        </ReactFlow>
      </div>
    </div>
  );
}
